// UserMenu — signed-in identity control for the app shell's top bar.
//
// Clicking the avatar opens a small dropdown with the signed-in member's name,
// a "Customize avatar" action (opens the character builder), and a Sign Out
// action (POST /auth/logout, then a full navigation to "/" so App.tsx's
// useProfile() gate re-runs and falls back to SignInPage).
import "./auth.css";
import type { ReactElement } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Avatar, Icon } from "@/features/hub-feed/design-atoms";
import { AvatarCustomizer } from "@/features/avatar";
import type { AvatarConfig } from "@/features/avatar";
import type { MemberProfile } from "@/lib/useProfile";

export interface UserMenuProps {
  profile: MemberProfile;
  /** Called after the user saves a new avatar so the shell can refresh the
   *  cached profile (updates this avatar instantly). */
  onAvatarSaved?: (avatar: AvatarConfig) => void;
}

export function UserMenu({ profile, onAvatarSaved }: UserMenuProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Close the customizer modal on Escape.
  useEffect(() => {
    if (!customizing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCustomizing(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [customizing]);

  const signOut = useCallback(() => {
    fetch("/auth/logout", { method: "POST", credentials: "same-origin" })
      .catch(() => {
        // Best-effort: even if the network call fails, still drop the client
        // back to the sign-in gate below.
      })
      .finally(() => {
        window.location.href = "/";
      });
  }, []);

  const handleSaved = useCallback(
    (avatar: AvatarConfig) => {
      setCustomizing(false);
      onAvatarSaved?.(avatar);
    },
    [onAvatarSaved],
  );

  return (
    <div className="auth-user-menu" ref={rootRef}>
      <button
        type="button"
        className="avatar-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Signed in as ${profile.name} — open account menu`}
        title={profile.name}
        onClick={() => setOpen((o) => !o)}
        data-testid="user-menu-trigger"
      >
        <Avatar
          id={profile.id}
          initials={profile.initials}
          name={profile.name}
          avatar={profile.avatar}
          size={38}
        />
      </button>
      {open && (
        <div className="auth-user-menu-dropdown" role="menu">
          <div className="auth-user-menu-name" role="none">
            <strong>{profile.name}</strong>
          </div>
          <button
            type="button"
            role="menuitem"
            className="auth-user-menu-item"
            onClick={() => {
              setOpen(false);
              setCustomizing(true);
            }}
            data-testid="user-menu-customize"
          >
            <Icon.star size={15} /> Customize avatar
          </button>
          <button
            type="button"
            role="menuitem"
            className="auth-user-menu-signout"
            onClick={signOut}
            data-testid="user-menu-signout"
          >
            Sign out
          </button>
        </div>
      )}

      {/* Portalled to document.body: the top bar sets `backdrop-filter`, which
          makes it a containing block for position:fixed descendants — a modal
          rendered inline here would be trapped inside the header instead of
          covering the viewport. */}
      {customizing && createPortal(
        <div
          className="hub-modal-overlay"
          data-testid="avatar-customizer-modal"
          onClick={() => setCustomizing(false)}
        >
          <div
            className="hub-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Customize your avatar"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hub-modal__head">
              <h2 className="hub-modal__title">Customize your avatar</h2>
              <button
                type="button"
                className="hub-modal__close"
                aria-label="Close"
                onClick={() => setCustomizing(false)}
              >
                <Icon.x size={18} />
              </button>
            </div>
            <AvatarCustomizer
              initial={profile.avatar}
              onSaved={handleSaved}
              onClose={() => setCustomizing(false)}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
